'use client';

import { z as zod } from 'zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { getErrorMessage } from '../../utils';
import { signUp } from '../../context/firebase';
import { FormCard, FormHead, SignOutButton } from '../../components';

// ----------------------------------------------------------------------

const SignUpSchema = (t) =>
  zod.object({
    firstName: zod.string().min(1, { message: t('First name is required') }),
    lastName: zod.string().min(1, { message: t('Last name required') }),
    email: zod
      .string()
      .min(1, { message: t('Email required') })
      .email({ message: t('Invalid email') }),
    password: zod
      .string()
      .min(1, { message: t('The password is required') })
      .min(8, { message: t('Password must be at least 8 characters') }),
  });

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

export function FirebaseSignUpView() {
  const router = useRouter();

  const showPassword = useBoolean();

  const searchParams = useSearchParams();

  const { t, currentLang } = useTranslate();

  const [errorMsg, setErrorMsg] = useState('');
  const [errorCode, setErrorCode] = useState('');

  const methods = useForm({
    resolver: zodResolver(SignUpSchema(t)),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      setErrorMsg('');
      await signUp({
        email: data.email,
        password: data.password,
        firstName: data.firstName?.trim().replace(/\s+/g, ' '),
        lastName: data.lastName?.trim().replace(/\s+/g, ' '),
      });
      router.refresh();
    } catch (error) {
      setErrorMsg(getErrorMessage(error));
      setErrorCode(error.code);
    }
  });

  const renderError = () => (
    <Alert severity="error" onClose={() => setErrorMsg('')}>
      {t(errorMsg)}.
      {errorCode === 'auth/credential-already-in-use' && (
        <SignOutButton size="small" fullWidth={false} title="Exit anonymous mode" />
      )}
    </Alert>
  );

  const renderForm = () => (
    <Box gap={3} display="flex" flexDirection="column">
      {!!errorMsg && renderError()}

      <Box display="flex" gap={{ xs: 3, sm: 2 }} flexDirection={{ xs: 'column', sm: 'row' }}>
        <Field.Text
          name="firstName"
          label={t('First name')}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Field.Text
          name="lastName"
          label={t('Last name')}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Box>

      <Field.Text name="email" label={t('Email')} slotProps={{ inputLabel: { shrink: true } }} />

      <Field.Text
        name="password"
        label={t('Password')}
        placeholder={t('+8 characters')}
        type={showPassword.value ? 'text' : 'password'}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={showPassword.onToggle} edge="end">
                  <Iconify icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Button
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        loadingIndicator={`${t('Creating account')}...`}
      >
        {t('Create account')}
      </Button>
    </Box>
  );

  return (
    <FormCard showTerms>
      <FormHead
        title={t('Sign up')}
        description={t('Enter your personal data to create your account')}
        slotProps={{ description: { sx: { mb: 1.5 } } }}
      />

      <Form methods={methods} onSubmit={onSubmit}>
        {renderForm()}
      </Form>

      <Box
        sx={{
          py: 0.5,
          gap: 0.5,
          display: 'flex',
          typography: 'body2',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {t('Already have an account?')}

        <Link
          color="inherit"
          component={RouterLink}
          href={`${paths.auth.firebase.signIn}${newSearchParams({ ...Object.fromEntries(searchParams), country: currentLang.countryName })}`}
        >
          {t('Sign in')}
        </Link>
      </Box>
    </FormCard>
  );
}
